import { ReaderVisitTracker } from './components/ReaderVisitTracker';
import { ViewAsBanner } from './components/ViewAsBanner';
import { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, NavigationType, UNSAFE_LocationContext as LocationContext, useLocation, useNavigationType, type Location } from 'react-router-dom';
import { useUser } from './providers/UserProvider';
import { useShortcutsContext } from './providers/ShortcutsProvider';
import { getPageShortcuts } from './utils/pageShortcuts';
import { isShortcutsHelpShortcut, shortcutKeyMatches } from './utils/keyboard';
import { LEGACY_DYNAMIC_ROUTES, LEGACY_STATIC_REDIRECTS, legacyRedirectTarget } from './utils/legacyRoutes';
import { AddLinkModal } from './components/AddLinkModal';
import { isModalOpen } from './components/ModalOverlay';
import { PrivateRoute } from './components/PrivateRoute';
import { AdminRoute } from './components/AdminRoute';
import { SuperAdminRoute } from './components/SuperAdminRoute';
// Eagerly imported: everything a logged-out or ordinary reader can land on.
// These belong in the first bundle because making them lazy would trade a
// download that already happened for a round trip on the critical path.
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import ReaderEmailPage from './pages/ReaderEmailPage';
import SettingsPage from './pages/SettingsPage';
import NotFoundPage from './pages/NotFoundPage';
import StoryPermalinkPage from './pages/StoryPermalinkPage';
import { isSentenceAdminHost, isSentenceHost } from './utils/sentenceHost';
import { isEvalsHost } from './utils/evalsHost';
import { ReaderFeedRoute, ReaderFeedsRoute, ReaderHomeRoute, ReaderSearchRoute } from './pages/ReaderEntryRoutes';

/*
 * Lazily imported: every page behind AdminRoute or SuperAdminRoute.
 *
 * The whole admin surface used to ship to every reader. One IIFE bundle
 * carried all 46 pages, so somebody arriving at /tldr downloaded the scoring
 * rule editor, the prompt lab and the pipeline console before the feed could
 * render. Splitting at the authorization boundary is the natural cut: a reader
 * who never sees these routes never fetches them, and an admin pays one small
 * chunk fetch on navigation, warm-connection and edge-cached.
 *
 * This is the entire reason the build emits ESM with --splitting. esbuild
 * cannot split an IIFE, and --splitting alone would not have helped either:
 * with one entry point and no dynamic import() in the graph there is nothing
 * to split *at*. These calls are the split points; the flag just lets esbuild
 * honor them.
 *
 * The boundary is load-bearing, so keep it honest. Each of these must be
 * imported here and nowhere else -- a static import of any of them from an
 * eager module silently pulls the page back into the main bundle, with no
 * error and no signal beyond the bundle growing again.
 */
const SitesPage = lazy(() => import('./pages/SitesPage'));
const SiteDetailPage = lazy(() => import('./pages/SiteDetailPage'));
const ArticleAdminPage = lazy(() => import('./pages/ArticleAdminPage'));
const QualityPromptPage = lazy(() => import('./pages/QualityPromptPage'));
const ChangeDetailPage = lazy(() => import('./pages/ChangeDetailPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const ReaderAnalyticsPage = lazy(() => import('./pages/ReaderAnalyticsPage'));
const PipelinePage = lazy(() => import('./pages/PipelinePage'));
const TopicsPage = lazy(() => import('./pages/TopicsPage'));
const ScoringSettingsPage = lazy(() => import('./pages/ScoringSettingsPage'));
const CategorySocialScoringPage = lazy(() => import('./pages/CategorySocialScoringPage'));
const QualityScoringPage = lazy(() => import('./pages/QualityScoringPage'));
const PromptLabPage = lazy(() => import('./pages/PromptLabPage'));
const PromptLabsHubPage = lazy(() => import('./pages/PromptLabsHubPage'));
const XAccountsPage = lazy(() => import('./pages/XAccountsPage'));
const DirectoryPage = lazy(() => import('./pages/DirectoryPage'));
const SourceScoresPage = lazy(() => import('./pages/SourceScoresPage'));
const RecentDiscoveredFeedsPage = lazy(() => import('./pages/RecentDiscoveredFeedsPage'));
const NewslettersPage = lazy(() => import('./pages/NewslettersPage'));
const AdminArxivPage = lazy(() => import('./pages/AdminArxivPage'));
const SentenceSearchPage = lazy(() => import('./pages/SentenceSearchPage'));
const SentenceDesignsPage = lazy(() => import('./pages/SentenceDesignsPage'));
const SentenceApp = lazy(() => import('./pages/sentences/SentenceApp'));
const SentenceAdminPage = lazy(() => import('./pages/sentences/SentenceAdminPage'));
const EvalsApp = lazy(() => import('./pages/evals/EvalsApp'));
const AdminHomePage = lazy(() => import('./pages/AdminHomePage'));
const StoriesPage = lazy(() => import('./pages/StoriesPage'));
const RecentSearchesPage = lazy(() => import('./pages/RecentSearchesPage'));
const AdminUserForYouPage = lazy(() => import('./pages/AdminUserForYouPage'));
const AdminAccountManagerFeedsPage = lazy(() => import('./pages/AdminAccountManagerFeedsPage'));
const SponsoredPlacementsPage = lazy(() => import('./pages/SponsoredPlacementsPage'));
const AdminEvalsPage = lazy(() => import('./pages/AdminEvalsPage'));
const AdminDocumentsPage = lazy(() => import('./pages/AdminDocumentsPage'));
const AdminPublicSearchesPage = lazy(() => import('./pages/AdminPublicSearchesPage'));

/**
 * Every pre-beta URL shape, resolved by `utils/legacyRoutes`. One component
 * rather than one per shape so the table there is the whole story.
 */
function LegacyRedirect() {
  const location = useLocation();
  const target = legacyRedirectTarget(location.pathname, location.search);
  return <Navigate to={target ?? '/'} replace />;
}

export function App() {
  const { isAdmin, isAdminUser, toggleAdminMode } = useUser();
  const { dynamicGroups } = useShortcutsContext();
  const location = useLocation();
  const navigationType = useNavigationType();
  const authOpen = ['/login', '/signup'].includes(location.pathname);
  // The account dialog draws over the page the reader came from, so the page
  // routes see that page's location while the URL reads /login or /signup.
  // The context is provided here rather than through `<Routes location>`:
  // that prop wraps the matched routes in a provider only while it is set, so
  // toggling it remounts the whole page on open and again on close, which
  // refetches the feed and races the story list's auto-focus against the
  // dialog's focus restore. A provider that is always present keeps the tree
  // stable, and it can carry the real navigation type when no dialog is open.
  const pageLocation: Location = authOpen
    ? (location.state?.backgroundLocation || { pathname: '/', search: '', hash: '', state: null, key: 'default' })
    : location;
  const [showShortcuts, setShowShortcuts] = useState(false);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (shortcutKeyMatches(e, '/') && (e.metaKey || e.ctrlKey)) { e.preventDefault(); setShowShortcuts(s => !s); }
      if (shortcutKeyMatches(e, 'h') && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const t = e.target as HTMLElement;
        if (t.tagName !== 'INPUT' && t.tagName !== 'TEXTAREA' && !t.isContentEditable) {
          e.preventDefault(); setShowShortcuts(s => !s);
        }
      }
      {
        const t = e.target as HTMLElement;
        const typing = t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable;
        if (isShortcutsHelpShortcut(e, typing)) { e.preventDefault(); setShowShortcuts(s => !s); }
      }
      // 'a' toggles admin mode for admins (issue #889). Non-admins keep 'a' as
      // the story expand key, so this listener only claims it for admins.
      if (isAdminUser && shortcutKeyMatches(e, 'a') && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const t = e.target as HTMLElement;
        if (t.tagName !== 'INPUT' && t.tagName !== 'TEXTAREA' && !t.isContentEditable && !isModalOpen()) {
          e.preventDefault();
          toggleAdminMode();
        }
      }
      if (e.key === 'Escape') setShowShortcuts(false);
    };
    const blur = () => setShowShortcuts(false);
    window.addEventListener('keydown', down);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('blur', blur);
    };
  }, [isAdminUser, toggleAdminMode]);

  const groups = [
    ...getPageShortcuts(location.pathname, {
      isAdmin,
      isAdminUser,
      keyboardShortcutsEnabled: !['/', '/digests'].includes(location.pathname),
    }),
    ...dynamicGroups,
  ].filter((g) => g.rows.length > 0);

  // sentence.tldr.fm is its own app (Top, For You, feeds, sign-in, design
  // variants), none of the reader around it; sentence-admin.tldr.fm is its
  // admin page at every path.
  if (isSentenceHost(window.location.hostname)) {
    return <Suspense fallback={null}><SentenceApp /></Suspense>;
  }
  if (isSentenceAdminHost(window.location.hostname)) {
    return <Suspense fallback={null}><SentenceAdminPage /></Suspense>;
  }
  // evals.tldr.fm is the admin eval browser and spot checks, for Kingfisher admins only.
  if (isEvalsHost(window.location.hostname)) {
    return <Suspense fallback={null}><EvalsApp /></Suspense>;
  }

  return (
    <>
    <AddLinkModal isAdmin={isAdmin} />
    {showShortcuts && groups.length > 0 && (
      <div className="shortcuts-overlay">
        {groups.map((group) => (
          <div key={group.heading} className="shortcuts-overlay-section">
            <div className="shortcuts-overlay-heading">{group.heading}</div>
            {group.rows.map((row, i) => (
              <div key={i} className="shortcuts-overlay-row">
                {row.keys.map((k) => <kbd key={k}>{k}</kbd>)}
                {' '}{row.label}
              </div>
            ))}
          </div>
        ))}
      </div>
    )}
    {/*
      * One boundary around the whole table rather than per route: only the
      * lazy admin pages can suspend, and they are mutually exclusive, so a
      * boundary per route would be 31 copies of the same thing.
      *
      * The fallback is deliberately empty. A chunk fetch on a warm connection
      * from a nearby edge is short enough that a spinner would flash in and
      * out and read as a glitch, and the eager reader routes never suspend, so
      * nothing on the critical path is ever waiting behind this.
      */}
    <Suspense fallback={null}>
    <>
    <ViewAsBanner />
    <ReaderVisitTracker />
    <div inert={authOpen} aria-hidden={authOpen || undefined}>
    <LocationContext.Provider value={{ location: pageLocation, navigationType: authOpen ? NavigationType.Pop : navigationType }}>
    <Routes>
      {/* The beta reader serves both logged-in and logged-out readers. */}
      {/* The reader: feeds v2 when signed in, snapshot-served when not (pages/ReaderEntryRoutes). */}
      <Route path="/" element={<ReaderHomeRoute />} />
      <Route path="/feeds" element={<ReaderFeedsRoute />} />
      <Route path="/feeds/:feedId" element={<ReaderFeedRoute />} />
      {/* One page for both: a text search's URL becomes its description id's in place, without a remount. */}
      <Route path="/search" element={<ReaderSearchRoute />} />
      <Route path="/search/:searchId" element={<ReaderSearchRoute />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      {/* Linked from backend password-reset emails (app/services/frontend_links.py);
          parity-pinned by backend/tests/test_frontend_route_parity.py. */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Linked from mailbox-confirmation emails (PRO-701); parity-pinned the same way. */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/story/:identifier" element={<StoryPermalinkPage />} />
      {/* The audit view stays admin-only even though the runs themselves are now
          open to every logged-in user: it exposes cut candidates, per-cover
          verdicts and donor provenance. */}
      {/* Pre-beta URL shapes. Redirected, never served, so old bookmarks and
          pasted links keep working; the table lives in utils/legacyRoutes. */}
      {Object.keys(LEGACY_STATIC_REDIRECTS).map((path) => (
        <Route key={path} path={path} element={<Navigate to={LEGACY_STATIC_REDIRECTS[path]} replace />} />
      ))}
      {LEGACY_DYNAMIC_ROUTES.map((path) => (
        <Route key={path} path={path} element={<LegacyRedirect />} />
      ))}
      <Route path="/admin/search" element={<AdminRoute><StoriesPage /></AdminRoute>} />
      <Route path="/admin" element={<AdminRoute><AdminHomePage /></AdminRoute>} />
      <Route path="/admin/directory" element={<AdminRoute><DirectoryPage /></AdminRoute>}>
        <Route index element={<Navigate to="sites" replace />} />
        <Route path="sites" element={<SitesPage hideShell />} />
        <Route path="recent-discoveries" element={<RecentDiscoveredFeedsPage hideShell />} />
        <Route path="articles" element={<Navigate to="/admin/search" replace />} />
        <Route path="x-accounts" element={<XAccountsPage hideShell />} />
        <Route path="users" element={<UsersPage hideShell />} />
        <Route path="source-scores" element={<SourceScoresPage hideShell />} />
      </Route>
      <Route path="/sites" element={<Navigate to="/admin/directory/sites" replace />} />
      <Route path="/sites/:siteId" element={<AdminRoute><SiteDetailPage /></AdminRoute>} />
      <Route path="/admin/articles" element={<Navigate to="/admin/search" replace />} />
      <Route path="/article/admin/:storyId" element={<AdminRoute><ArticleAdminPage /></AdminRoute>} />
      <Route path="/admin/change/:changeId" element={<AdminRoute><ChangeDetailPage /></AdminRoute>} />
      <Route path="/users" element={<Navigate to="/admin/directory/users" replace />} />
      <Route path="/admin/reader-analytics" element={<AdminRoute><ReaderAnalyticsPage /></AdminRoute>} />
      <Route path="/admin/newsletters" element={<AdminRoute><NewslettersPage /></AdminRoute>} />
      <Route path="/admin/arxiv" element={<AdminRoute><AdminArxivPage /></AdminRoute>} />
      {/* Public, no login: the page sentence.tldr.fm opens on. */}
      <Route path="/sentence-search" element={<SentenceSearchPage />} />
      <Route path="/sentence-search/:design" element={<SentenceDesignsPage />} />
      <Route path="/admin/sponsored-placements" element={<AdminRoute><SponsoredPlacementsPage /></AdminRoute>} />
      <Route path="/admin/evals" element={<AdminRoute><AdminEvalsPage /></AdminRoute>} />
      <Route path="/admin/evals/:type" element={<AdminRoute><AdminEvalsPage /></AdminRoute>} />
      <Route path="/admin/evals/:type/:caseId" element={<AdminRoute><AdminEvalsPage /></AdminRoute>} />
      <Route path="/admin/documents" element={<AdminRoute><AdminDocumentsPage /></AdminRoute>} />
      <Route path="/admin/topics/*" element={<AdminRoute><TopicsPage /></AdminRoute>} />
      <Route path="/admin/pipeline" element={<SuperAdminRoute><PipelinePage /></SuperAdminRoute>} />
      <Route path="/admin/x-accounts" element={<Navigate to="/admin/directory/x-accounts" replace />} />
      <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
      <Route path="/admin/scoring-settings" element={<SuperAdminRoute><ScoringSettingsPage /></SuperAdminRoute>} />
      <Route path="/admin/category-social-scoring" element={<SuperAdminRoute><CategorySocialScoringPage /></SuperAdminRoute>} />
      <Route path="/admin/quality-scoring" element={<SuperAdminRoute><QualityScoringPage /></SuperAdminRoute>} />
      <Route path="/admin/quality-scoring/prompts/:promptId" element={<SuperAdminRoute><QualityPromptPage /></SuperAdminRoute>} />
      <Route path="/admin/prompt-labs" element={<SuperAdminRoute><PromptLabsHubPage /></SuperAdminRoute>} />
      {/* Linked from backend default-prompt alarm emails (app/services/frontend_links.py);
          parity-pinned by backend/tests/test_frontend_route_parity.py. */}
      <Route path="/admin/prompt-lab/:promptType" element={<SuperAdminRoute><PromptLabPage /></SuperAdminRoute>} />
      <Route path="/admin/recent-searches" element={<AdminRoute><RecentSearchesPage /></AdminRoute>} />
      <Route path="/for-you/user/:userId" element={<AdminRoute><AdminUserForYouPage /></AdminRoute>} />
      <Route path="/admin/account-manager-feeds" element={<AdminRoute><AdminAccountManagerFeedsPage /></AdminRoute>} />
      <Route path="/admin/public-searches" element={<AdminRoute><AdminPublicSearchesPage /></AdminRoute>} />
      {/* Path pinned to backend frontend_links.DIGESTS_PATH: every digest
          email footer links here. */}
      <Route path="/digests" element={<PrivateRoute><ReaderEmailPage /></PrivateRoute>} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </LocationContext.Provider>
    </div>
    {authOpen && <Routes><Route path="/login" element={<LoginPage />} /><Route path="/signup" element={<SignupPage />} /></Routes>}
    </>
    </Suspense>
    </>
  );
}
